import type { Config } from 'tailwindcss';
import plugin from 'tailwindcss/plugin';

const ignoreTwinMacroFiles = (content: string) => {
  if (content.match(/twin\.macro/)) {
    return '';
  }

  return content;
};

const FONT_FALLBACKS = [
  /* --- 1. System UI Fonts (Latin Fallbacks) --- */
  // If 'Inter-Fallback' (Arial) fails or isn't found, these ensure English text
  // still looks like the native OS interface.
  '-apple-system', // San Francisco (macOS/iOS)
  'BlinkMacSystemFont', // San Francisco (older WebKit)
  'Segoe UI', // Windows
  'Roboto', // Android & ChromeOS (Critical: Android often lacks Arial)
  'Helvetica Neue', // macOS (Pre-San Francisco)
  'Arial', // Universal Basic Sans

  /* --- 2. CJK Fallbacks (Glyph Coverage) --- */
  // Inter & Poppins do not contain Korean, Japanese, or Chinese characters.
  // The browser skips the fonts above (which also lack these characters)
  // and hunts down this list until it finds a font that can render the glyph.

  // Japanese
  'Hiragino Sans', // macOS/iOS
  'Yu Gothic', // Windows

  // Chinese (Simplified)
  'Microsoft YaHei', // Windows
  'PingFang SC', // macOS/iOS

  // Korean
  'Apple SD Gothic Neo', // macOS/iOS
  'Malgun Gothic', // Windows

  /* --- 3. Final Generic Safety Net --- */
  'sans-serif',
];

export default {
  content: {
    files: [
      './src/{api,assets,components,constants,context,gatsby,hooks,layouts,pages,styles,templates,utils}/**/*.{js,jsx,ts,tsx}',
    ],
    transform: {
      ts: ignoreTwinMacroFiles,
      tsx: ignoreTwinMacroFiles,
    },
  },
  darkMode: 'selector',
  plugins: [
    plugin(function ({ addUtilities }) {
      addUtilities({
        '.container-stack-context': {
          position: 'relative',
          zIndex: '1',
        },
      });
    }),
  ],
  theme: {
    container: {
      center: true,
    },
    extend: {
      boxShadow: {
        small: '6px 6px 0px 0px #290042',
        large: '12px 12px 0px 0px #290042',
      },
    },
    borderWidth: {
      DEFAULT: '1px',
      0: '0',
      2: '2px',
      3: '3px',
      4: '4px',
      6: '6px',
      8: '8px',
    },
    colors: {
      transparent: 'transparent',
      primary: {
        p1: '#290042', // Persistence purple
        p2: '#D3121D', // Sonar red
      },
      secondary: {
        s1: '#126ED3', // Qube blue
      },
      tertiary: {
        t1: '#FBACBE', // Bubblegum pink
        t2: '#1B998B', // Sour apple green
        t3: '#FFE156', // Lemon zest yellow
      },
      'ui-colours': {
        ui1: '#EEF4FC', // Ghost white for background
        ui2: '#0C5DB5', // Dark blue for hover state
        ui3: '#B7D3F2', // Uranium blue for disabled state
        ui4: '#FFF6F7', // Light red for error state
        ui5: '#4F3E5A', // Hover purple
        ui6: '#D4CDD9', // Disabled purple
        ui7: '#DDEAF9', // Robyns Egg Blue
      },
      neutrals: {
        n1: '#FFFFFF',
        n2: '#F4F7FB',
        n3: '#E8EFF7',
        n4: '#DDE6F3',
        n5: '#D3DCE9',
        n6: '#A8B3C2',
        n7: '#69809B',
        n8: '#3B3F44',
        n9: '#000000',
      },
      utils: {
        transparent: '#0000ffff',
      },
    },
    fontFamily: {
      // 'Inter' must match the font-family name in your @font-face CSS
      inter: ['Inter', 'Inter-Fallback', ...FONT_FALLBACKS],

      // 'Poppins' must match the font-family name in your @font-face CSS
      poppins: ['Poppins', 'Poppins-Fallback', ...FONT_FALLBACKS],
    },
    fontSize: {
      'display-xxl': [
        '80px',
        {
          letterSpacing: '2px',
          lineHeight: '88px',
        },
      ],
      'display-xl': ['48px', '58px'],
      'heading-l': ['40px', '48px'],
      'heading-m': ['32px', '40px'],
      'heading-s': ['22px', '28px'],
      'heading-xs': ['21px', '28px'],
      'heading-xxs': ['18px', '24px'],
      'body-xl': ['24px', '32px'],
      'body-l': ['20px', '30px'],
      'body-m': ['18px', '28px'],
      'body-s': ['16px', '20px'],
      'body-xs': ['14px', '21px'],
      'body-xxs': ['10px', '13px'],
      'tag-l': ['16px', '18px'],
      'tag-m': [
        '16px',
        {
          letterSpacing: '2.4px',
          lineHeight: '17px',
        },
      ],
      'tag-s': [
        '12px',
        {
          letterSpacing: '1.8px',
          lineHeight: '14px',
        },
      ],
      'text-cta': [
        '16px',
        {
          letterSpacing: '2.4px',
          lineHeight: '18px',
        },
      ],
      'mobile-display-xxl': [
        '48px',
        {
          letterSpacing: '2px',
          lineHeight: '56px',
        },
      ],
      'mobile-display-xl': ['40px', '48px'],
      'mobile-heading-l': ['32px', '40px'],
      'mobile-heading-m': ['26px', '32px'],
      'mobile-heading-s': ['21px', '28px'],
      'mobile-heading-xs': ['18px', '24px'],
      'mobile-heading-xxs': ['16px', '21px'],
      'mobile-body-xl': ['24px', '32px'],
      'mobile-body-l': ['20px', '30px'],
      'mobile-body-m': ['18px', '28px'],
      'mobile-body-s': ['10px', '16px'],
      'mobile-tag-l': ['16px', '18px'],
      'mobile-tag-m': [
        '16px',
        {
          letterSpacing: '2.4px',
          lineHeight: '17px',
        },
      ],
      'mobile-tag-s': [
        '12px',
        {
          letterSpacing: '1.8px',
          lineHeight: '14px',
        },
      ],
      'mobile-text-cta': [
        '16px',
        {
          letterSpacing: '2.4px',
          lineHeight: '18px',
        },
      ],
      // ALL THE PRICING SPECIFIC FONT SIZES ARE NOT USED ANYWHERE
      // ALL VALUES ARE HARDCODED IN THE COMPONENTS (text-[00px]), WE SHOULD USE THEM INSTEAD
      'pricing-heading-s': ['20px', '25px'],
      'pricing-heading-xxs': ['18px', '24px'],
      'pricing-body-l': ['18px', '24px'],
      'pricing-tag-s': [
        '12px',
        {
          letterSpacing: '1.8px',
          lineHeight: '14px',
        },
      ],
      'pricing-tag-xs': [
        '11px',
        {
          letterSpacing: '1.65px',
          lineHeight: '13px',
        },
      ],
      'mobile-pricing-heading-s': ['20px', '26px'],
      'mobile-pricing-heading-xxs': ['18px', '24px'],
      'mobile-pricing-body-l': ['18px', '24px'],
      'mobile-pricing-tag-s': [
        '12px',
        {
          letterSpacing: '1.8px',
          lineHeight: '14px',
        },
      ],
      'mobile-pricing-tag-xs': [
        '11px',
        {
          letterSpacing: '1.65px',
          lineHeight: '13px',
        },
      ],
    },
    fontWeight: {
      bold: '700',
      'semi-bold': '600',
      medium: '500',
      regular: '400',
    },
    letterSpacing: {
      normal: '0',
      extra: '0.15em',
    },
    maxWidth: {
      // Useful for elements max-width for sm, md & lg
      // usage example: max-w-lg
      sm: '288px',
      md: '720px',
      lg: '1120px',
      7: '1280px',
    },
    minWidth: {
      // Useful for elements min-width for sm, md & lg
      // usage example: min-w-lg
      sm: '288px',
      md: '720px',
      lg: '1120px',
      full: '100%',
      screen: '100vw',
    },
    minHeight: {
      'm-500': '500px',
      full: '100%',
      screen: '100vh',
    },
    order: {
      first: '-9999',
      last: '9999',
      none: '0',
      1: '1',
      2: '2',
      3: '3',
      4: '4',
    },
    screens: {
      // the screens values are going to be used
      // as breakpoints by the 'container'
      sm: '500px',
      md: '720px',
      lg: '1120px',
      shortHeight: { raw: '(max-height: 400px)' },
    },
    spacing: {
      0: '0',
      1: '8px',
      2: '16px',
      3: '24px',
      4: '32px',
      5: '40px',
      6: '48px',
      7: '56px',
      8: '64px',
      9: '72px',
      10: '80px',
      11: '88px',
      12: '96px',
    },
    width: {
      // icons width
      16: '16px',
      32: '32px',
      64: '64px',
      // defaults tailwind
      '1/2': '50%',
      '1/3': '33.333333%',
      '2/3': '66.666667%',
      '1/4': '25%',
      '2/4': '50%',
      '3/4': '75%',
      '1/5': '20%',
      '2/5': '40%',
      '3/5': '60%',
      '4/5': '80%',
      '1/6': '16.666667%',
      '2/6': '33.333333%',
      '3/6': '50%',
      '4/6': '66.666667%',
      '5/6': '83.333333%',
      '1/12': '8.333333%',
      '2/12': '16.666667%',
      '3/12': '25%',
      '4/12': '33.333333%',
      '5/12': '41.666667%',
      '6/12': '50%',
      '7/12': '58.333333%',
      '8/12': '66.666667%',
      '9/12': '75%',
      '10/12': '83.333333%',
      '11/12': '91.666667%',
      auto: 'auto',
      full: '100%',
      fullscreen: '100vw',
      min: 'min-content',
      max: 'max-content',
      fit: 'fit-content',
    },
  },
  corePlugins: {
    accentColor: false,
    animation: false,
    aspectRatio: false,
    backgroundAttachment: false,
    backgroundBlendMode: false,
    backgroundClip: false,
    backgroundOrigin: false,
    borderCollapse: true,
    borderOpacity: false,
    borderSpacing: true,
    breakAfter: false,
    breakBefore: false,
    brightness: false,
    caretColor: false,
    clear: false,
    contrast: false,
    divideColor: false,
    divideOpacity: false,
    divideStyle: false,
    divideWidth: false,
    fontVariantNumeric: false,
    hueRotate: false,
    invert: false,
    isolation: false,
    justifyItems: true,
    justifySelf: false,
    mixBlendMode: false,
    objectFit: true,
    objectPosition: false,
    outlineColor: false,
    outlineOffset: false,
    outlineWidth: false,
    overscrollBehavior: false,
    placeContent: false,
    placeSelf: false,
    placeholderColor: false,
    placeholderOpacity: false,
    resize: false,
    ringColor: false,
    ringOffsetColor: false,
    ringOffsetWidth: false,
    ringOpacity: false,
    ringWidth: false,
    saturate: false,
    scrollPadding: false,
    scrollSnapAlign: false,
    scrollSnapStop: false,
    scrollSnapType: false,
    sepia: false,
    skew: false,
    space: false,
    strokeWidth: false,
    textDecorationColor: false,
    textDecorationStyle: false,
    textDecorationThickness: false,
    textIndent: false,
    textOpacity: false,
    textUnderlineOffset: false,
    touchAction: false,
  },
} satisfies Config;
